import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    Paper,
    Snackbar,
    TextField,
    Typography,
} from "@mui/material";
import React, { ChangeEvent, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { WarehouseBody } from "../../../@types/DTO/warehousebody";
import {
    FranchiseAdminDto,
    GetAllWarehouseListApi,
} from "../../../api/GetWarehouseList/GetAllWarehouseListApi";
import { useLoading } from "../../../views/Loader/LoadingContext";

interface CreateWarehouseProps {
  onBack: () => void;
}

interface CityOption {
  cityId: string;
  cityName: string;
}

interface WarehouseTypeOption {
  id: number;
  warehouseType: string;
}

const CreateWarehouse: React.FC<CreateWarehouseProps> = ({
  onBack,
}) => {
  const [newItem, setNewItem] = useState<WarehouseBody>({
    warehouseName: "",
    city: "",
    hubType: "",
  });
  const [shortName, setShortName] = useState("");
  const [companyName, setCompanyName] = useState("");

  // Franchise hub state
  const [isFranchiseHub, setIsFranchiseHub] = useState(false);
  const [franchiseAdminOptions, setFranchiseAdminOptions] = useState<FranchiseAdminDto[]>([]);
  const [selectedFranchiseOwner, setSelectedFranchiseOwner] = useState<FranchiseAdminDto | null>(null);
  const [priceMultiplier, setPriceMultiplier] = useState("");

  const { setLoading } = useLoading();
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });
  const navigate = useNavigate();

  const [cityOptions, setCityOptions] = useState<CityOption[]>([]);
  const [hubTypeOptions, setHubTypeOptions] = useState<WarehouseTypeOption[]>([]);
  const [selectedCity, setSelectedCity] = useState<CityOption | null>(null);
  const [selectedHubType, setSelectedHubType] = useState<WarehouseTypeOption | null>(null);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await GetAllWarehouseListApi.api.getAllCityList(0, 100);
        if (response.data && response.data.cityList) {
          setCityOptions(response.data.cityList);
        }
      } catch (error) {
        console.error("Error fetching city list:", error);
        setSnackbar({
          open: true,
          message: "Failed to load city list",
          severity: "error",
        });
      }
    };

    const fetchWarehouseTypes = async () => {
      try {
        const response = await GetAllWarehouseListApi.api.getAllWarehouseType(0, 100);
        if (response.data && response.data.warehouseTypeList) {
          setHubTypeOptions(response.data.warehouseTypeList);
        }
      } catch (error) {
        console.error("Error fetching warehouse type list:", error);
        setSnackbar({
          open: true,
          message: "Failed to load warehouse types",
          severity: "error",
        });
      }
    };

    fetchCities();
    fetchWarehouseTypes();
  }, []);

  // Fetch franchise admins only when checkbox is ticked (lazy load)
  useEffect(() => {
    if (!isFranchiseHub) return;
    if (franchiseAdminOptions.length > 0) return;

    const fetchFranchiseAdmins = async () => {
      try {
        const response = await GetAllWarehouseListApi.api.getFranchiseAdmins();
        if (response.data) {
          setFranchiseAdminOptions(response.data);
        }
      } catch (error) {
        console.error("Error fetching franchise admins:", error);
        setSnackbar({
          open: true,
          message: "Failed to load franchise owners",
          severity: "error",
        });
      }
    };

    fetchFranchiseAdmins();
  }, [isFranchiseHub, franchiseAdminOptions.length]);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string
  ) => {
    const value = e.target.value;
    setNewItem({ ...newItem, [field]: value });
  };

  const handleFranchiseHubToggle = (e: ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setIsFranchiseHub(checked);
    if (!checked) {
      // Reset dependent fields when unticked
      setSelectedFranchiseOwner(null);
      setPriceMultiplier("");
    }
  };

  const handleCreateWarehouse = async () => {
    // Validate required fields
    if (!newItem.warehouseName || !selectedCity || !selectedHubType || !shortName || !companyName) {
      setSnackbar({
        open: true,
        message: "Please fill in all required fields",
        severity: "error",
      });
      return;
    }

    // Validate franchise-specific fields when checkbox is ticked
    if (isFranchiseHub && (!selectedFranchiseOwner || !priceMultiplier)) {
      setSnackbar({
        open: true,
        message: "Please fill in franchise owner and price multiplier",
        severity: "error",
      });
      return;
    }

    setLoading(true);
    try {
      const requestBody = {
        name: newItem.warehouseName,
        // code: "",
        shortName: shortName,
        companyName: companyName,
        warehouseTypeId: selectedHubType.id,
        cityId: selectedCity.cityId,
        isFranchiseHub: isFranchiseHub,
        franchiseOwner: isFranchiseHub && selectedFranchiseOwner ? selectedFranchiseOwner.userId : null,
        priceMultiplier: isFranchiseHub && priceMultiplier ? Number(priceMultiplier) : null,
      };
      
      console.log("Creating warehouse with payload:", requestBody);
      await GetAllWarehouseListApi.api.createWarehouse(requestBody);
      
      setSnackbar({
        open: true,
        message: "Warehouse created successfully",
        severity: "success",
      });
      setNewItem({ 
        warehouseName: "",
        city: "",
        hubType: "",
      });
      setShortName("");
      setCompanyName("");
      setSelectedCity(null);
      setSelectedHubType(null);
      setIsFranchiseHub(false);
      setSelectedFranchiseOwner(null);
      setPriceMultiplier("");
      setTimeout(() => {
        navigate("/warehouse");
      }, 2000);
    } catch (error: any) {
      console.error("Warehouse creation error details:", error);
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          "Error creating warehouse";
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    }
    setLoading(false);
  };
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ padding: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            fontWeight: "600",
            color: "#1E1E1E",
            textAlign: "left",
            fontSize: "1.5rem",
            mb: 1,
          }}>
          New Warehouse Details
        </Typography>
        <div className="flex items-center bg-gray-100 px-4 py-2 rounded-md space-x-2">
          <Link
            to="/home"
            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors">
            Dashboard
          </Link>
          <span className="text-sm text-gray-400">/</span>
          <Link
            to="/warehouse"
            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors">
            Warehouse
          </Link>
          <span className="text-sm text-gray-400">/</span>
          <span className="text-sm font-medium text-gray-700">
            Create Warehouse
          </span>
        </div>
      </Box>

      {/* Form */}
      <Paper
        sx={{
          padding: 3,
          marginTop: 2,
          borderRadius: "8px",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
        }}>
        <Grid container spacing={3} sx={{ textAlign: "left" }}>
          <Grid item xs={12} sm={4}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Warehouse Name
            </Typography>
            <TextField
              fullWidth
              value={newItem.warehouseName}
              onChange={(e) => handleInputChange(e, "warehouseName")}
              variant="standard"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Short Name
            </Typography>
            <TextField
              fullWidth
              value={shortName}
              onChange={(e) => setShortName(e.target.value)}
              variant="standard"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Company Name
            </Typography>
            <TextField
              fullWidth
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              variant="standard"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              City
            </Typography>
            <Autocomplete
              options={cityOptions}
              value={selectedCity}
              onChange={(event, value) => setSelectedCity(value)}
              getOptionLabel={(option) => option.cityName}
              renderInput={(params) => (
                <TextField {...params} variant="standard" />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Hub Type
            </Typography>
            <Autocomplete
              options={hubTypeOptions}
              value={selectedHubType}
              onChange={(event, value) => setSelectedHubType(value)}
              getOptionLabel={(option) => option.warehouseType}
              renderInput={(params) => (
                <TextField {...params} variant="standard" />
              )}
            />
          </Grid>

          {/* Franchise Hub Checkbox */}
          <Grid item xs={12} sm={4} sx={{ display: "flex", alignItems: "center" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isFranchiseHub}
                  onChange={handleFranchiseHubToggle}
                />
              }
              label="Is Franchise Hub"
            />
          </Grid>

          {/* Franchise-only fields, shown only when checkbox is ticked */}
          {isFranchiseHub && (
            <>
              <Grid item xs={12} sm={4}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  sx={{ fontWeight: "500", mb: 1 }}>
                  Franchise Owner
                </Typography>
                <Autocomplete
                  options={franchiseAdminOptions}
                  value={selectedFranchiseOwner}
                  onChange={(event, value) => setSelectedFranchiseOwner(value)}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option.userId === value.userId}
                  renderInput={(params) => (
                    <TextField {...params} variant="standard" />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  sx={{ fontWeight: "500", mb: 1 }}>
                  Price Multiplier
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={priceMultiplier}
                  onChange={(e) => setPriceMultiplier(e.target.value)}
                  variant="standard"
                />
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* Actions */}
      <Box sx={{ marginTop: 4, textAlign: "right" }}>
        <Box>
          <Button
            variant="contained"
            onClick={handleCreateWarehouse}
            color="primary"
            sx={{
              padding: "10px 20px",
              borderRadius: "32px",
              backgroundColor: "#1E1E1E",
              textTransform: "none",
              fontWeight: "500",
              "&:hover": { backgroundColor: "#000000" },
            }}>
            Create Warehouse
          </Button>
        </Box>
      </Box>
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateWarehouse;